import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, X, CheckCircle2, Circle, Mail, Upload, Send,
  ClipboardList, ArrowRight, Eye,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import ProjectAuctionPanel from "@/components/ProjectAuctionPanel";

// Step type → colour helper
const TYPE_BADGE = {
  auto: "bg-zinc-700/40 text-zinc-400 border-zinc-700",
  action: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
  check: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

const stepIcon = (s) => {
  if (s.completed) return <CheckCircle2 size={14} className="text-[#00E575]" />;
  return <Circle size={14} className="text-zinc-500" />;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// ───────────────────────────────────────────────────────────────────────
// Drilldown drawer with full 15-step timeline + actions
// ───────────────────────────────────────────────────────────────────────
function WorkflowDrawer({ projectId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyStep, setBusyStep] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/back-office/projects/${projectId}/workflow`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Workflow konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const checkStep = async (stepKey, note = "") => {
    setBusyStep(stepKey);
    try {
      const r = await api.post(`/back-office/projects/${projectId}/steps/${stepKey}/check`, { note });
      setData((d) => ({ ...d, ...r.data }));
      toast.success("Schritt abgehakt");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Abhaken");
    } finally {
      setBusyStep(null);
    }
  };

  const uncheckStep = async (stepKey) => {
    setBusyStep(stepKey);
    try {
      const r = await api.post(`/back-office/projects/${projectId}/steps/${stepKey}/uncheck`);
      setData((d) => ({ ...d, ...r.data }));
      toast.success("Schritt zurückgesetzt");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusyStep(null);
    }
  };

  const sendBndRequest = async () => {
    setBusyStep("bnd_requested");
    try {
      const r = await api.post(`/back-office/projects/${projectId}/steps/bnd-request/send`);
      setData((d) => ({ ...d, ...r.data }));
      toast.success(r.data?.ok ? "BnD-Anfrage versendet" : `Versand fehlgeschlagen: ${r.data?.error || ""}`);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusyStep(null);
    }
  };

  const uploadBnd = async (file) => {
    if (!file) return;
    setBusyStep("bnd_received");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/back-office/projects/${projectId}/steps/bnd/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({ ...d, ...r.data }));
      toast.success("BnD-PDF hochgeladen");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusyStep(null);
    }
  };

  const forwardBnd = async () => {
    setBusyStep("bnd_forwarded");
    try {
      const r = await api.post(`/back-office/projects/${projectId}/steps/bnd-forward/send`);
      setData((d) => ({ ...d, ...r.data }));
      toast.success(r.data?.ok ? "BnD an Kunden weitergeleitet" : `Versand fehlgeschlagen: ${r.data?.error || ""}`);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusyStep(null);
    }
  };

  // Render the right-side button for a step depending on type + key
  const renderAction = (step) => {
    if (step.completed && step.type === "check") {
      return (
        <button
          onClick={() => uncheckStep(step.key)}
          disabled={busyStep === step.key}
          data-testid={`step-uncheck-${step.key}`}
          className="h-8 px-2.5 rounded-md bg-zinc-800/60 hover:bg-zinc-800 text-zinc-400 text-[12px] disabled:opacity-50"
        >
          Zurücksetzen
        </button>
      );
    }
    if (step.completed) {
      return null;
    }
    // Not completed — render type-specific action
    if (step.type === "check") {
      return (
        <button
          onClick={() => checkStep(step.key)}
          disabled={busyStep === step.key}
          data-testid={`step-check-${step.key}`}
          className="h-9 px-3 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busyStep === step.key ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Erledigt
        </button>
      );
    }
    // action steps
    if (step.key === "bnd_requested") {
      return (
        <button
          onClick={sendBndRequest}
          disabled={busyStep === step.key}
          data-testid="step-action-bnd-request"
          className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busyStep === step.key ? <Loader2 size={12} className="animate-spin" /> : <Mail size={13} />}
          BnD anfordern
        </button>
      );
    }
    if (step.key === "bnd_forwarded") {
      const hasBnd = Boolean((data?.back_office?.steps?.bnd_received || {}).storage_path);
      return (
        <button
          onClick={forwardBnd}
          disabled={!hasBnd || busyStep === step.key}
          title={hasBnd ? "" : "BnD muss zuerst hochgeladen werden"}
          data-testid="step-action-bnd-forward"
          className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          {busyStep === step.key ? <Loader2 size={12} className="animate-spin" /> : <Send size={13} />}
          An Kunde senden
        </button>
      );
    }
    if (["order_confirmation_sent", "bza_package_sent", "az1_sent", "az2_sent", "schlussrechnung_sent"].includes(step.key)) {
      return (
        <button
          onClick={() => toast.info('Diese Aktion wird im Projekt-Detail ausgelöst (Tab „Status & Versand")')}
          data-testid={`step-action-redirect-${step.key}`}
          className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
        >
          <ArrowRight size={12} /> Im Projekt veranlassen
        </button>
      );
    }
    return null;
  };

  // Extra: upload field for BnD reception
  const bndReceived = data?.back_office?.steps?.bnd_received || {};
  const bndReceivedCompleted = data?.steps?.find((s) => s.key === "bnd_received")?.completed;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl max-h-[95vh] bg-[#0A0A0B] border border-zinc-800 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        data-testid="back-office-drawer"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 bg-[#09090B]">
          <div className="min-w-0">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-semibold">Back-Office Workflow</div>
            <div className="text-[14px] text-zinc-100 font-bold truncate">
              {data ? `${data.completed_count}/${data.total} Schritte erledigt` : "Lade…"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800" data-testid="close-drawer">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        {/* Steps timeline */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          <ProjectAuctionPanel projectId={projectId} />
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#00E575]" size={20} />
            </div>
          )}
          {!loading && data?.steps?.map((step, idx) => {
            const isCurrent = step.key === data.current_step_key;
            return (
              <div
                key={step.key}
                data-testid={`step-row-${step.key}`}
                className={`relative rounded-xl border p-3 ${
                  step.completed
                    ? "bg-[#00E575]/5 border-[#00E575]/20"
                    : isCurrent
                    ? "bg-[#18181B] border-[#00E575]/40 ring-1 ring-[#00E575]/20"
                    : "bg-[#09090B] border-zinc-800"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center pt-1">
                    {stepIcon(step)}
                    {idx < data.steps.length - 1 && (
                      <div className={`w-px h-6 mt-1 ${step.completed ? "bg-[#00E575]/40" : "bg-zinc-800"}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] text-zinc-300 font-mono">{String(idx + 1).padStart(2, "0")}</span>
                      <span className={`text-[13px] font-semibold ${step.completed ? "text-zinc-300" : "text-zinc-100"}`}>
                        {step.label}
                      </span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border ${TYPE_BADGE[step.type]}`}>
                        {step.type}
                      </span>
                      {isCurrent && !step.completed && (
                        <span className="text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-[#00E575] text-[#09090B]">aktuell</span>
                      )}
                    </div>
                    {(step.completed_at || step.completed_by_name || step.note) && (
                      <div className="text-[10.5px] text-zinc-500 mt-1 flex flex-wrap gap-x-2">
                        {step.completed_at && <span>{fmtDateTime(step.completed_at)}</span>}
                        {step.completed_by_name && <span>· {step.completed_by_name}</span>}
                        {step.note && <span>· {step.note}</span>}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {renderAction(step)}
                      {step.key === "bnd_received" && (
                        <>
                          <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold cursor-pointer">
                            {busyStep === "bnd_received" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                            {bndReceivedCompleted ? "Neue Version" : "BnD hochladen"}
                            <input
                              type="file" accept="application/pdf,.pdf" className="hidden"
                              data-testid="bnd-upload-input"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBnd(f); e.target.value = ""; }}
                            />
                          </label>
                          {bndReceived?.storage_path && (
                            <a
                              href={fileUrl(bndReceived.storage_path)}
                              target="_blank" rel="noopener noreferrer"
                              className="h-9 px-2.5 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[11.5px] inline-flex items-center gap-1"
                              data-testid="bnd-download"
                            >
                              <Eye size={11} /> {bndReceived.filename || "BnD"}
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-zinc-800 bg-[#09090B] flex items-center justify-between gap-2">
          <button
            onClick={() => window.open(`/projects/${projectId}`, "_blank")}
            data-testid="open-project"
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold inline-flex items-center gap-1.5"
          >
            <ArrowRight size={12} /> Projekt öffnen
          </button>
          <button
            onClick={load}
            className="h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1 font-medium"
          >
            <RefreshCw size={11} /> Aktualisieren
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Main Kanban page
// ───────────────────────────────────────────────────────────────────────
export default function BackOffice() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/back-office/projects");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Liste konnte nicht geladen werden");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation im System.
  useReloadBus("*", () => load(true));

  const columns = useMemo(() => {
    if (!data) return [];
    const map = {};
    for (const card of data.projects) {
      (map[card.current_step_key] ||= []).push(card);
    }
    return data.step_definition.map((s) => ({
      ...s,
      cards: map[s.key] || [],
    }));
  }, [data]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <div className="px-4 sm:px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-[20px] sm:text-[24px] font-bold inline-flex items-center gap-2">
              <ClipboardList size={20} className="text-[#00E575]" /> Back-Office Cockpit
            </h1>
            <p className="text-[12px] text-zinc-300 mt-0.5 font-medium">
              15-Schritte-Workflow nach Angebots-Signatur · Kanban zeigt den aktuellen Schritt jedes Projekts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-zinc-300 font-medium" data-testid="bo-total">
              {data ? `${data.projects.length} aktive Projekte` : ""}
            </span>
            <button
              onClick={load}
              className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1.5 font-medium"
              data-testid="bo-refresh"
            >
              <RefreshCw size={12} /> Aktualisieren
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-[#00E575]" size={22} />
        </div>
      )}

      {!loading && data && data.projects.length === 0 && (
        <div className="px-4 py-16 text-center text-zinc-500" data-testid="bo-empty">
          Aktuell sind keine signierten Aufträge im Back-Office. Sobald ein Kunde ein Angebot
          unterschreibt, erscheint er hier automatisch.
        </div>
      )}

      {!loading && data && data.projects.length > 0 && (
        <div className="overflow-x-auto pb-6">
          <div className="flex gap-3 px-4 sm:px-6 min-w-max" data-testid="bo-kanban">
            {columns.map((col, idx) => (
              <div
                key={col.key}
                data-testid={`bo-col-${col.key}`}
                className="w-[280px] flex-shrink-0 rounded-xl bg-[#0F0F11] border border-zinc-800 flex flex-col max-h-[calc(100vh-160px)]"
              >
                {/* Column header */}
                <div className="px-3 py-2.5 border-b border-zinc-800/80 flex items-center gap-2 sticky top-0 bg-[#0F0F11] rounded-t-xl">
                  <span className="text-[9px] font-mono text-zinc-300">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="flex-1 text-[11.5px] font-semibold text-zinc-200 truncate" title={col.label}>{col.label}</span>
                  <span className="text-[12px] font-bold rounded-full bg-zinc-800 px-1.5 py-0.5 text-zinc-300">
                    {col.cards.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {col.cards.length === 0 && (
                    <div className="text-[10.5px] text-zinc-600 text-center py-4">—</div>
                  )}
                  {col.cards.map((c) => (
                    <button
                      key={c.project_id}
                      onClick={() => setSelectedProjectId(c.project_id)}
                      data-testid={`bo-card-${c.project_id}`}
                      className="w-full text-left rounded-lg bg-[#18181B] hover:bg-[#1F1F24] border border-zinc-800 hover:border-[#00E575]/40 p-2.5 transition group"
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 text-[12.5px] font-semibold text-zinc-100 truncate">{c.customer_name}</div>
                        {c.sales_handover?.done_at && (
                          <span className="text-[8px] font-bold uppercase tracking-wider rounded px-1 py-0.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 flex-shrink-0" title="Sales-Handover erfolgt">
                            ÜG
                          </span>
                        )}
                      </div>
                      <div className="text-[10.5px] text-zinc-500 truncate mt-0.5">
                        {[c.customer_zip, c.customer_city].filter(Boolean).join(" ")}
                      </div>
                      <div className="mt-1.5 h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#00E575] transition-all"
                          style={{ width: `${(c.completed_count / c.total) * 100}%` }}
                        />
                      </div>
                      <div className="text-[12px] text-zinc-300 mt-1">{c.completed_count}/{c.total}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedProjectId && (
        <WorkflowDrawer
          projectId={selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
        />
      )}
    </div>
  );
}
